import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { DayPassOfferDto } from '@gc/shared';

import { ApiError } from '@/lib/api';
import { createDayPassOffer, iqdAmountOf, minutesOf, updateDayPassOffer } from './dayPasses';

/**
 * Add or edit an offer (SPEC §6.1) — the centered-modal pattern of
 * `RecordSafeTransactionDialog`.
 *
 * "Until closing" is a RADIO rather than an empty minutes box, because the two
 * are different promises and an empty field reads as an unfinished form. A
 * fixed window promises N minutes; an open one promises the day, and the two
 * answer differently the moment a venue changes its closing time mid-pass.
 */
export function OfferDialog({
  offer,
  onClose,
  onSaved,
}: {
  /** Null for a new offer. */
  offer: DayPassOfferDto | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();

  const [name, setName] = useState(offer?.name ?? '');
  const [priceRaw, setPriceRaw] = useState(offer ? (BigInt(offer.priceMd) / 1000n).toString() : '');
  const [untilClosing, setUntilClosing] = useState(offer ? offer.durationMinutes === null : true);
  const [minutesRaw, setMinutesRaw] = useState(offer?.durationMinutes?.toString() ?? '');
  const [isActive, setIsActive] = useState(offer?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const priceIqd = iqdAmountOf(priceRaw);
  const durationMinutes = untilClosing ? null : minutesOf(minutesRaw);
  const ready =
    name.trim() !== '' && priceIqd !== null && (untilClosing || durationMinutes !== null);

  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!ready || priceIqd === null) return;
    setError(null);
    setSubmitting(true);
    try {
      const payload = { name: name.trim(), priceIqd, durationMinutes, isActive };
      if (offer) await updateDayPassOffer(offer.id, payload);
      else await createDayPassOffer(payload);
      onSaved();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('dayPasses.offerForm.failed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        role="dialog"
        aria-modal="true"
        aria-label={t(offer ? 'dayPasses.offerForm.editTitle' : 'dayPasses.offerForm.createTitle')}
        className="w-full max-w-md rounded-card border border-subtle bg-ground p-5 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-content">
          {t(offer ? 'dayPasses.offerForm.editTitle' : 'dayPasses.offerForm.createTitle')}
        </h2>

        <label className="mt-4 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('dayPasses.offerForm.name')}
          </span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={120}
            required
            placeholder={t('dayPasses.offerForm.namePlaceholder')}
            className={inputClass}
          />
        </label>

        <label className="mt-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('dayPasses.offerForm.price')}
          </span>
          <input
            value={priceRaw}
            // Digits only: money is bigint millidinars on the wire, and a typed
            // decimal here is how a float creeps into a price.
            onChange={(event) => setPriceRaw(event.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            autoComplete="off"
            className={`${inputClass} tabular`}
          />
          <span className="mt-1 block text-xs text-content-muted">
            {t('dayPasses.offerForm.priceHint')}
          </span>
        </label>

        <fieldset className="mt-3">
          <legend className="mb-1 text-sm text-content-secondary">
            {t('dayPasses.offerForm.window')}
          </legend>
          <label className="flex items-center gap-2 py-1 text-sm text-content">
            <input
              type="radio"
              name="window"
              checked={untilClosing}
              onChange={() => setUntilClosing(true)}
              className="h-4 w-4 accent-[var(--brand-primary)]"
            />
            {t('dayPasses.offerForm.windowUntilClosing')}
          </label>
          <label className="flex items-center gap-2 py-1 text-sm text-content">
            <input
              type="radio"
              name="window"
              checked={!untilClosing}
              onChange={() => setUntilClosing(false)}
              className="h-4 w-4 accent-[var(--brand-primary)]"
            />
            {t('dayPasses.offerForm.windowFixed')}
          </label>

          {!untilClosing ? (
            <input
              value={minutesRaw}
              onChange={(event) => setMinutesRaw(event.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              aria-label={t('dayPasses.offerForm.durationMinutes')}
              className={`${inputClass} tabular mt-2`}
            />
          ) : null}
        </fieldset>

        <label className="mt-3 flex items-center gap-2 text-sm text-content">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
            className="h-4 w-4 accent-[var(--brand-primary)]"
          />
          {t('dayPasses.offerForm.activeLabel')}
        </label>

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('dayPasses.offerForm.cancel')}
          </button>
          <button
            type="submit"
            disabled={!ready || submitting}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {submitting ? t('common.loading') : t('dayPasses.offerForm.save')}
          </button>
        </div>
      </form>
    </div>
  );
}
